import { useMemo } from "react";
import { useNodes } from "@xyflow/react";
import { layoutGraph } from "../graph/layout";
import type { NodeGeometry, NodeSize } from "../graph/layout";
import type { LayoutItem } from "../graph/types";
import { nodeId } from "../graph/types";
import { useRootStore } from "../../../stores/hooks";

/** Positions nodes from measured sizes. Before RF measures a node,
 *  `measured` is undefined and layout falls back to DEFAULT_CARD_HEIGHT /
 *  CARD_WIDTH; once measurements arrive this recomputes and nodes settle
 *  (real widths matter: narrow nodes must be centered by their own width or
 *  their edge handles skew off the stack axis). The canvas is kept invisible
 *  until the first fully-measured layout (see index.tsx). */
export const useFlowLayout = (
  layout: LayoutItem[],
): { geometries: Map<string, NodeGeometry>; allMeasured: boolean } => {
  const store = useRootStore();
  const liveNodes = useNodes();
  const sizesKey = liveNodes
    .map((n) => `${n.id}:${n.measured?.width ?? 0}x${n.measured?.height ?? 0}`)
    .join("|");
  const allMeasured =
    liveNodes.length > 0 &&
    liveNodes.every((n) => n.measured?.height !== undefined);

  // Loop headers are measured out-of-band (they're absolutely positioned, so
  // RF's box measurement of the loop node reflects the layout-assigned height,
  // not the wrapped header). Keyed by instanceId in the store; re-key the memo
  // on any change so the layout reflows when a name wraps to a new line.
  const loopHeaderHeights = store.flowCanvas.loopHeaderHeights;
  const loopHeaderKey = [...loopHeaderHeights]
    .map(([id, height]) => `${id}:${height}`)
    .join("|");

  const geometries = useMemo(() => {
    const sizes = new Map<string, NodeSize>();
    liveNodes.forEach((n) => {
      if (n.measured?.height !== undefined) {
        sizes.set(n.id, {
          width: n.measured.width,
          height: n.measured.height,
        });
      }
    });
    const headerByNodeId = new Map<string, number>();
    loopHeaderHeights.forEach((height, instanceId) => {
      headerByNodeId.set(nodeId.loopGroup(instanceId), height);
    });
    return layoutGraph(layout, sizes, headerByNodeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, sizesKey, loopHeaderKey]);

  return { geometries, allMeasured };
};
